import interpolateComponents from '@automattic/interpolate-components';
import { useMemo, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { Button } from 'components/Button/Button';
import { H4 } from 'components/Text/H4';
import { Text } from 'components/Text/Text';
import { getMainChartActiveLayer } from 'modules/salaries/helpers/getMainChartActiveLayer';
import { getMainChartDetails } from 'modules/salaries/helpers/getMainChartDetails';
import { TitleDescriptionModal } from 'modules/salaries/modals/TitleDescription/TitleDescriptionModal';
import { MainChartData } from 'modules/salaries/types/MainChartData';
import { Color } from 'theme/theme';

import { ChartDetail } from './ChartDetail';
import {
  ChartDetailsContainer,
  LeftSection,
  RightSection,
  SplitSection,
  StyledTooltip,
} from './MainChartDetails.styled';

function formatSalary(salary: number): string {
  return salary.toLocaleString(CURRENT_LOCALE(), {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

type Props = {
  data: MainChartData;
};

export function MainChartDetails({ data }: Props) {
  const [showModal, setShowModal] = useState(false);

  const activeChart = getMainChartActiveLayer(data);

  const iconColor: Color =
    activeChart === 'estimatedRange' ? 'brandBlue' : 'brandGreen';

  const chartDetails = useMemo(
    () => getMainChartDetails(data, activeChart),
    [data, activeChart],
  );

  const { filtersUsed, submittedData } = data;
  const { cityBucket, yearsOfExperience } = filtersUsed || {};
  const locationNotApplied =
    activeChart === 'submissions' &&
    cityBucket !== 'CITY' &&
    submittedData?.city;
  const yearsOfExperienceNotApplied =
    activeChart === 'submissions' &&
    submittedData &&
    yearsOfExperience &&
    submittedData.yearsOfExperience &&
    !yearsOfExperience.includes(submittedData.yearsOfExperience);

  const locationTooltip = locationNotApplied ? (
    <StyledTooltip
      green={activeChart === 'submissions'}
      greyColor
      qaId="location-field-tooltip"
    >
      {cityBucket === 'NATIONAL'
        ? getText(
            "We don't have enough data yet for your location, so we expanded the search to display national data for the United States.",
          )
        : getText(
            "We don't currently have enough data yet for your location, so we expanded the search to include other cities with a similar average annual wage.",
          )}
    </StyledTooltip>
  ) : null;

  const yearsOfExperienceTooltip = yearsOfExperienceNotApplied ? (
    <StyledTooltip
      green={activeChart === 'submissions'}
      greyColor
      qaId="years-of-experience-field-tooltip"
    >
      {yearsOfExperience
        ? sprintf(
            getText(
              "We don't have enough data yet within that range for Years of Experience, so we have expanded it to include salary submissions from people with %(yearsOfExperienceUsedMin)s-%(yearsOfExperienceUsedMax)s years of experience.",
            ),
            {
              yearsOfExperienceUsedMin: yearsOfExperience[0],
              yearsOfExperienceUsedMax: yearsOfExperience[1],
            },
          )
        : getText(
            "We don't have enough data yet within that range for Years of Experience, so we have expanded it to include salary submissions from people with any number of years of experience.",
          )}
    </StyledTooltip>
  ) : null;

  return (
    <SplitSection data-qa-id="main-chart-details">
      <LeftSection>
        <H4>{getText('Your Key Inputs')}</H4>
        <ChartDetailsContainer>
          <ChartDetail iconColor={iconColor}>
            <Button
              type="button"
              data-qa-id="job-title-button"
              noPadding
              onClick={() => setShowModal(true)}
              variant="link"
              size="large"
            >
              <Text textAlign="left">{chartDetails.jobTitle}</Text>
            </Button>
            {showModal ? (
              <TitleDescriptionModal
                onClose={() => setShowModal(false)}
                jobDescription={submittedData?.jobDescription ?? ''}
                jobTitle={submittedData?.jobTitleSelected ?? ''}
              />
            ) : null}
          </ChartDetail>
          <ChartDetail
            iconColor={locationNotApplied ? 'brandYellow' : iconColor}
            iconName={locationNotApplied ? 'double-arrow-small' : undefined}
            tooltip={locationTooltip}
          >
            {chartDetails.location}
          </ChartDetail>
          <ChartDetail
            iconColor={yearsOfExperienceNotApplied ? 'brandYellow' : iconColor}
            iconName={
              yearsOfExperienceNotApplied ? 'double-arrow-small' : undefined
            }
            tooltip={yearsOfExperienceTooltip}
          >
            {chartDetails.yearsOfExperience}
          </ChartDetail>
        </ChartDetailsContainer>
      </LeftSection>
      <RightSection>
        <H4>Understanding Your Results</H4>
        <ChartDetailsContainer>
          <ChartDetail iconColor={iconColor} iconName="us-money-filled">
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  'Most Common Salary Range: {{strong}}%(mostCommonSalaryMin)s to %(mostCommonSalaryMax)s{{/strong}}',
                ),
                {
                  mostCommonSalaryMin: formatSalary(
                    chartDetails.mostCommonSalaryMin,
                  ),
                  mostCommonSalaryMax: formatSalary(
                    chartDetails.mostCommonSalaryMax,
                  ),
                },
              ),
              components: {
                strong: <strong />,
              },
            })}
          </ChartDetail>
          <ChartDetail iconColor={iconColor} iconName={chartDetails.chartIcon}>
            {chartDetails.description}
          </ChartDetail>
        </ChartDetailsContainer>
      </RightSection>
    </SplitSection>
  );
}
